import React, { useState, useEffect } from 'react';
import { today } from '../lib/utils';
import { TEAM, DOMAINS } from '../lib/constants';

const MONTHS = ['march','april','may','june'];

export default function TaskModal({ open, task, defaultDomain, defaultMonth, onClose, onSave }) {
  const [form, setForm] = useState(emptyForm(defaultDomain, defaultMonth));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        id: task.id,
        title: task.title || '',
        desc: task.desc || '',
        domain: task.domain || 'tech',
        owner: task.owner || 'Arshad',
        priority: task.priority || 'P1',
        status: task.status || 'pending',
        deadline: task.deadline || '',
        month: task.month || defaultMonth,
        eta: task.eta || '',
        reason: task.reason || '',
        link: task.link || '',
      });
    } else {
      setForm(emptyForm(defaultDomain, defaultMonth));
    }
    setErrors({});
  }, [open, task, defaultDomain, defaultMonth]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isOverdue = form.deadline && new Date(form.deadline) < today && form.status === 'in_progress';

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required.';
    if (form.status === 'in_progress' && !form.deadline) e.deadline = 'Deadline required for "In progress" tasks.';
    if (isOverdue) {
      if (!form.eta) e.eta = 'New ETA required — original deadline passed.';
      if (!form.reason.trim()) e.reason = 'Reason for delay required.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.modal}>
        <div style={styles.head}>
          <div>
            <h2 style={styles.title}>{task?.id ? 'Edit task' : 'New task'}</h2>
            <p style={styles.sub}>Mandatory fields are enforced before save.</p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div style={styles.body}>
          <Field label="Title" required error={errors.title}>
            <input style={inputStyle(errors.title)} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Ship nurse-station rollout for Cytecare" />
          </Field>

          <Field label="Description / context">
            <textarea style={{ ...inputStyle(), minHeight:70, resize:'vertical' }} value={form.desc} onChange={e => set('desc', e.target.value)} placeholder="Why this matters. Acceptance criteria." />
          </Field>

          <div style={styles.row}>
            <Field label="Domain" required>
              <select style={inputStyle()} value={form.domain} onChange={e => set('domain', e.target.value)}>
                {DOMAINS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Owner" required>
              <select style={inputStyle()} value={form.owner} onChange={e => set('owner', e.target.value)}>
                {TEAM.map(n => <option key={n}>{n}</option>)}
              </select>
            </Field>
          </div>

          <div style={styles.row}>
            <Field label="Priority" required>
              <select style={inputStyle()} value={form.priority} onChange={e => set('priority', e.target.value)}>
                <option value="P0">P0 — Critical</option>
                <option value="P1">P1 — High</option>
                <option value="P2">P2 — Medium</option>
                <option value="P3">P3 — Low</option>
              </select>
            </Field>
            <Field label="Status">
              <select style={inputStyle()} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
              </select>
            </Field>
          </div>

          <div style={styles.row}>
            <Field label={form.status==='in_progress' ? 'Deadline *' : 'Deadline'} error={errors.deadline}
              hint={form.status==='in_progress' ? 'Required while status is "In progress".' : 'Optional unless status is "In progress".'}
            >
              <input type="date" style={inputStyle(errors.deadline)} value={form.deadline} onChange={e => set('deadline', e.target.value)} />
            </Field>
            <Field label="Month">
              <select style={inputStyle()} value={form.month} onChange={e => set('month', e.target.value)}>
                {MONTHS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
              </select>
            </Field>
          </div>

          {isOverdue && (
            <>
              <Field label="New ETA *" required error={errors.eta}
                hint="Original deadline crossed. Provide a realistic new completion date."
                hintWarn
              >
                <input type="date" style={inputStyle(errors.eta)} value={form.eta} onChange={e => set('eta', e.target.value)} />
              </Field>
              <Field label="Reason for delay *" required error={errors.reason}
                hint="What's blocking? Be honest — this is for clarity, not blame."
                hintWarn
              >
                <textarea style={{ ...inputStyle(errors.reason), minHeight:70, resize:'vertical' }} value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="What's blocking?" />
              </Field>
            </>
          )}

          <Field label="Attach link (Figma · Doc · Sheet · GitHub)">
            <input style={inputStyle()} value={form.link} onChange={e => set('link', e.target.value)} placeholder="https://…" />
          </Field>
        </div>

        <div style={styles.foot}>
          <div style={styles.footNote}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            Auto-logged: timestamp · who edited
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onClose} style={styles.btnSec}>Cancel</button>
            <button onClick={handleSave} style={styles.btnPrimary}>Save task</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, hint, hintWarn, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.1em', color:'#3D5878', marginBottom:7, fontFamily:"'JetBrains Mono',monospace" }}>
        {label}
        {required && <span style={{ color:'#EF4444', fontSize:13 }}>*</span>}
      </label>
      {children}
      {error && <div style={{ fontSize:11.5, color:'#EF4444', marginTop:4 }}>{error}</div>}
      {hint && !error && <div style={{ fontSize:11.5, color: hintWarn ? '#B45309' : '#7A8BA3', marginTop:4 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = (error) => ({
  width:'100%', padding:'10px 12px', background:'#F7FAFD',
  border: `1px solid ${error ? '#EF4444' : '#E4ECF4'}`, borderRadius:9,
  fontSize:13.5, color:'#0A2540', outline:'none', fontFamily:"'Geist',sans-serif",
  transition:'all .15s ease',
});

function emptyForm(domain, month) {
  return { id:'', title:'', desc:'', domain: domain||'tech', owner:'Arshad', priority:'P1', status:'pending', deadline:'', month: month||'april', eta:'', reason:'', link:'' };
}

const styles = {
  backdrop: { position:'fixed', inset:0, background:'rgba(10,37,64,.4)', backdropFilter:'blur(4px)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:20, animation:'fadeIn .2s ease' },
  modal: { background:'white', borderRadius:18, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 64px -16px rgba(10,37,64,.3)', animation:'modalIn .3s cubic-bezier(.4,0,.2,1)' },
  head: { padding:'22px 26px 18px', borderBottom:'1px solid #E4ECF4', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 },
  title: { fontFamily:"'Fraunces',serif", fontWeight:500, fontSize:22, letterSpacing:'-0.02em', margin:0 },
  sub: { marginTop:4, fontSize:13, color:'#3D5878' },
  closeBtn: { width:30, height:30, display:'grid', placeItems:'center', borderRadius:8, color:'#7A8BA3', background:'none', border:'none', cursor:'pointer' },
  body: { padding:'22px 26px' },
  row: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 },
  foot: { padding:'14px 26px', borderTop:'1px solid #E4ECF4', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'#F7FAFD', borderRadius:'0 0 18px 18px' },
  footNote: { fontSize:11.5, color:'#7A8BA3', display:'flex', alignItems:'center', gap:6 },
  btnSec: { display:'inline-flex', alignItems:'center', padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:500, background:'white', border:'1px solid #E4ECF4', color:'#0A2540', cursor:'pointer' },
  btnPrimary: { display:'inline-flex', alignItems:'center', padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:500, background:'#0A2540', color:'white', border:'none', cursor:'pointer' },
};
